<script setup lang="ts">

import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/core/components/ui/tooltip";
import BaseCardHeader from "@/core/components/base/BaseCardHeader.vue";
import BaseContainer from "@/core/components/base/BaseContainer.vue";
import BaseCard from "@/core/components/base/BaseCard.vue";
import BaseSelect from "@/core/components/base/BaseSelect.vue";
import BaseCardBody from "@/core/components/base/BaseCardBody.vue";
import {computed, ComputedRef, reactive} from "vue";
import {SortItem} from "vuetify/lib/components/VDataTable/composables/sort";
import useSwr from "@/core/composables/swr/useSwr";
import {cacheKey} from "@/lib/cache";
import {asafoApi} from "@/container";
import {ASAFOSearchParams, FraudReport, FraudReportCustomer} from "@/core/interfaces/fraud/fraud";
import BaseButton from "@/core/components/base/BaseButton.vue";
import SimpleIndicator from "@/patient-administration/components/SimpleIndicator.vue";
import {Info} from "@lucide/vue";
import dateHelper from "@/core/helpers/dateHelper";
import {Button} from "@/core/components/ui/button";
import {useRouter} from "vue-router";
import formatHelper from "@/core/helpers/formatHelper";
import SearchInput from "@/core/components/search-bar/SearchInput.vue";
import debounce from "lodash.debounce";
import ASAFOHeader from "@/asafo/components/ASAFOHeader.vue";

const router = useRouter();

const params = reactive<{
    size: number;
    page: number;
    sort: undefined | SortItem[],
    region: 'all' | number,
    is_customer: 'all' | 1 | 0,
    search?: string;
}>({
    size: 10,
    page: 1,
    sort: undefined,
    region: 'all',
    is_customer: 'all',
    search: '',
});

const headers = [
    {
        title: "Patient",
        key: "patient"
    },
    {
        title: "Signalements",
        key: "reports",
    },
    {
        title: "Region",
        key: "region",
    },
    {
        title: "Patient de la pharmacie ?",
        key: "is_customer",
    }
];

/**
 * Filter default params.
 */
const filteredParams: ComputedRef<ASAFOSearchParams> = computed(() => Object.keys(params)
    .filter(key => params[key] !== 'all' && params[key] !== undefined && params[key] !== '')
    .reduce((obj, key) => {
        obj[key] = params[key];
        return obj;
    }, {})
);

/**
 * Fetches fraud reports from the ASAFO API.
 * Uses SWR for data fetching and caching.
 */
const {data, isValidating} = useSwr(
    computed(() => cacheKey(`fraud-report-customers`, {...filteredParams.value})),
    () => asafoApi().customers(filteredParams.value),
);

/**
 * Debounced search function to filter reports based on keyword.
 */
const search = debounce((keyword: string) => {
    if (keyword.length > 3 || !keyword) {
        params.page = 1;
        params.sort = undefined;
        params.search = keyword;
    }
}, 300);

/**
 * Navigates to the reports page for a specific patient.
 * @param patient
 */
const showReports = (patient: FraudReportCustomer) => {
    router.push({name: 'asafo.customer', params: {id: patient.id}});
};

/**
 * Computed property to group reports by customer.
 */
const mappedCustomers: ComputedRef<{
    patient: FraudReportCustomer,
    reports: FraudReport[],
    region: string,
    is_customer: boolean,
}[]> = computed(() => {
    return data.value?.data.map((customer) => ({
        patient: customer,
        reports: customer.reports,
        region: customer.reports.map(report => report.entity.region_name).filter((e, i, self) => i === self.indexOf(e)).join(', '),
        is_customer: !!customer.customers.length,
    })) ?? [];
});
</script>

<template>
  <div>
    <ASAFOHeader />
    <div class="flex flex-col gap-6 p-6">
      <BaseCard v-if="data?.meta">
        <BaseCardHeader>Statistiques</BaseCardHeader>
        <BaseCardBody class="flex gap-4">
          <SimpleIndicator label="Dans l'officine" :value="data?.meta.of_pharmacy ?? 0" color="#e6d5f6" />
          <SimpleIndicator label="En région" :value="data?.meta.of_region ?? 0" color="#c7f2e8" />
          <SimpleIndicator label="En France" :value="data?.meta.of_france ?? 0" color="#f4f3f8" />
        </BaseCardBody>
      </BaseCard>
      <BaseContainer>
        <BaseCard class="overflow-visible">
          <BaseCardHeader>
            <div class="flex flex-1 items-center justify-between h-8">
              <div class="flex gap-4 items-center">
                Patients signalés
              </div>
              <div class="flex flex-row gap-2 items-end">
                <SearchInput placeholder="Nom ou NIR ..." :is-loading="false" @input="search" />
                <BaseSelect
                  v-model:model-value="params.region"
                  trigger-class="w-[180px]"
                  :options="[
                    {label: 'Toutes les régions', value: 'all'},
                    ... (data?.filters.regions || []).map(region => ({
                      label: region.name,
                      value: region.id.toString(),
                    })),
                  ]"
                />
                <BaseSelect
                  v-model:model-value="params.is_customer"
                  trigger-class="w-[180px]"
                  :options="[
                    {label: 'Tous les patients', value: 'all'},
                    {label: 'Dans mon officine', value: '1'},
                  ]"
                />
              </div>
            </div>
          </BaseCardHeader>
          <BaseCardBody>
            <v-data-table-server
              v-model:items-per-page="params.size"
              v-model:page="params.page"
              v-model:sort-by="params.sort"
              disable-sort
              return-object
              :headers="headers"
              :loading="isValidating"
              :items="mappedCustomers ?? []"
              :items-length="data?.meta.total ?? 0"
              :items-per-page-options="[10, 20, 50, 100]"
            >
              <template #[`item.patient`]="{item}">
                <div class="flex gap-2 w-56">
                  <TooltipProvider>
                    <Tooltip :delay-duration="200">
                      <TooltipTrigger class="flex flex-col" @click="() => showReports(item.patient)">
                        <div class="flex gap-2 items-center">
                          <p class="text-primary font-bold">
                            {{ formatHelper.patientName(item.patient) }}
                          </p>
                          <Info :size="16" />
                        </div>
                        <div class="text-slate-500">
                          {{ dateHelper.age(item.patient.birth_date) }}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent class="text-sm" avoid-collisions>
                        <div>
                          <p>NIR: {{ item.patient.nir }}</p>
                          <p>Né le {{ dateHelper.dayOnlyLongFormat(item.patient.birth_date) }}</p>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </template>
              <template #[`item.reports`]="{item}">
                <div class="flex gap-2 items-center min-w-32">
                  <TooltipProvider>
                    <Tooltip :delay-duration="200">
                      <TooltipTrigger class="flex gap-2">
                        <div class="flex gap-1 items-center">
                          <p>{{ item.patient.reports.length }}</p>
                          <Info :size="16" />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent class="text-sm" avoid-collisions>
                        <p v-for="report in item.reports" :key="report.asafo_key">
                          <span v-if="report.type == 'FAL'">Falsification</span>
                          <span v-if="report.type == 'DUP'">Duplication</span>
                          le {{ dateHelper.dayOnlyLongFormat(report.prescription_date) }}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <div class="text-left">
                    <Button class="bg-white h-6 w-12" @click="() => showReports(item.patient)">
                      Voir
                    </Button>
                  </div>
                </div>
              </template>
              <template #[`item.is_customer`]="{item}">
                <div class="flex gap-2 items-center min-w-32">
                  <BaseButton
                    v-if="item.is_customer" primary density="compact"
                    @click="() => $router.push({name: 'customer.home', params: {customerId: item.patient.customers[0].id}})"
                  >
                    <div class="text-sm">
                      Fiche patient
                    </div>
                  </BaseButton>
                </div>
              </template>
            </v-data-table-server>
          </BaseCardBody>
        </BaseCard>
      </BaseContainer>
    </div>
  </div>
</template>
